import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PensionAccountType } from 'apps/user/types/on-boarding.type';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import {
  AssignTrusteeDto,
  BeneficiariesAllocationDto
} from 'service/dto/general.dto';
import {
  AssignBeneficiaryDto,
  PensionAccount
} from 'service/dto/pension-account.dto';

type QueryParams = {
  pensionId: number;
};

export const useCreatePensionAccount = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (pension: PensionAccountType): Promise<void> => {
      return services.pensionService.createPension(pension);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-pension']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetPension = ({ pensionId }: QueryParams) => {
  const pensionAccount = useQuery<PensionAccount, AxiosError<Error>>(
    ['get-pension'],
    () => services.pensionService.getPensionAccount(pensionId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!pensionId
    }
  );

  return pensionAccount;
};

export const useGetPensionList = () =>
  useQuery(
    ['pension-accounts'],
    () => services.pensionService.getPensionAccounts(),
    {
      refetchOnWindowFocus: false
    }
  );

export const useDeletePension = () =>
  useMutation(
    (pensionId: number) =>
      services.pensionService.deletePensionAccount(pensionId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useAssignPensionBeneficiary = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (assignBeneficiaryDto: AssignBeneficiaryDto): Promise<void> => {
      return services.pensionService.assignBeneficiary(assignBeneficiaryDto);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assign-pension-beneficiary']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetPensionDetails = ({ pensionId }: { pensionId: number }) => {
  return useQuery<PensionAccount, AxiosError<Error>>(
    ['get_pension', pensionId],
    () => services.pensionService.getPensionAccount(pensionId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!pensionId
    }
  );
};

export const useUnAssignPensionBeneficiary = () =>
  useMutation(
    ({ allocationId }: { allocationId?: number }) =>
      services.pensionService.unAssignPensionBeneficiary(allocationId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const usePensionAllocationBulkEdit = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (beneficiariesAllocationDto: BeneficiariesAllocationDto): Promise<void> => {
      return services.pensionService.editBulkPensionBeneficiary(
        beneficiariesAllocationDto
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pension-allocation-edit']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useRemovePensionTrustee = () =>
  useMutation(
    ({ assetId, trusteeId }: { assetId: number; trusteeId: number }) =>
      services.pensionService.removePensionTrustee(assetId, trusteeId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useAssignPensionTrustee = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ assetId, trusteeId, note }: AssignTrusteeDto): Promise<void> => {
      return services.pensionService.assignPensionTrustee(
        assetId,
        trusteeId,
        note
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assign-pension-trustee']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};
